import { Checkbox, FormControl, FormControlLabel, Link, Radio, RadioGroup } from '@mui/material';
import React, { FC, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { MainModalCreate } from '../../MainModal';
import Input from '../../components/Input/Input';
import { FormValuesFromFields, genderTable, InputFields, normalizeAndValidateDate, RawField, withValidation } from '../../forms';

type TPatientRegister = {
  dataset: {
    patientHospitalPatientId: string;
    patientName: string;
    patientGender: 'other' | 'male' | 'female';
    patientBirthdate: string;
    patientEmail: string;
    patientEmailConfirm: string;
    hospitalName: string;
    confirmationPage: boolean;
    termsPrivacyLink: string;
    termsEulaLink: string;
  };
};

const PatientRegister: FC<TPatientRegister> = ({ dataset }) => {
  const FormFields = {
    hospitalPatientId: { ...InputFields.hospitalPatientId },
    patientName: { ...InputFields.patientName, required: true },
    gender: { ...InputFields.gender, required: true },
    birthdate: { ...InputFields.birthdate, required: true },
    email: { ...InputFields.email },
  } as const satisfies Record<string, RawField>;

  type PatientFormValues = FormValuesFromFields<typeof FormFields>;

  const formRef = useRef<HTMLFormElement | null>(null);

  dataset.confirmationPage = !!dataset.confirmationPage;
  const trigger = useRef(true);
  const [checked, setChecked] = useState(false);
  const methods = useForm<PatientFormValues>({
    defaultValues: {
      hospitalPatientId: dataset.patientHospitalPatientId || '',
      patientName: dataset.patientName || '',
      gender: dataset.patientGender || 'other',
      birthdate: dataset.patientBirthdate || '',
      email: dataset.patientEmail || '',
    },
  });
  const {
    formState: { errors },
    getValues,
    clearErrors,
    control,
  } = methods;

  const onSubmit = withValidation(FormFields, methods, (_, evt) => {
    if (trigger.current) {
      evt?.preventDefault();
      trigger.current = false;
      const values = getValues();
      MainModalCreate(
        {
          modalName: 'patientRegisterConfirmation',
          hospitalPatientId: values.hospitalPatientId,
          name: values.patientName,
          gender: values.gender,
          birthDate: values.birthdate,
          email: values.email,
          onAccept: (_, disableModal) => {
            if (disableModal) disableModal(true);
            formRef.current?.requestSubmit();
          },
          onReject: () => {
            trigger.current = true;
          },
        },
        { padding: '2rem' },
      );
      return;
    }
  });

  return (
    <form
      className='register-form'
      method='POST'
      onSubmit={(evt) => {
        onSubmit({}, evt);
      }}
      ref={formRef}
    >
      <h2>BrainSuite{dataset.confirmationPage ? ' 受検者登録情報の確認' : ' のお申し込み'}</h2>
      <h3>{dataset.hospitalName}</h3>
      {dataset.confirmationPage ? <p>登録情報をご確認ください。</p> : null}

      <Controller
        name={FormFields.hospitalPatientId.name}
        control={control}
        rules={{ required: FormFields.hospitalPatientId.required }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            label={FormFields.hospitalPatientId.label}
            errorText={fieldState.error?.message}
            inputProps={{
              tabIndex: FormFields.hospitalPatientId.tabIndex,
              required: FormFields.hospitalPatientId.required,
              maxLength: FormFields.hospitalPatientId.maxLength,
              pattern: FormFields.hospitalPatientId.pattern,
            }}
            type={FormFields.hospitalPatientId.type}
            size='small'
            fullWidth
            readonly
          />
        )}
      />

      <Controller
        name={FormFields.patientName.name}
        control={control}
        rules={{ required: FormFields.patientName.required }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            label={FormFields.patientName.label}
            errorText={fieldState.error?.message}
            inputProps={{
              tabIndex: FormFields.patientName.tabIndex,
              required: FormFields.patientName.required,
              maxLength: FormFields.patientName.maxLength,
              pattern: FormFields.patientName.pattern,
            }}
            type={FormFields.patientName.type}
            size='small'
            fullWidth
          />
        )}
      />

      <FormControl variant='outlined' size='small' error={!!errors.gender}>
        <Controller
          name={FormFields.gender.name}
          control={control}
          rules={{ required: FormFields.gender.required }}
          render={({ field }) => (
            <RadioGroup sx={{ justifyContent: 'start' }} row {...field}>
              <FormControlLabel
                value='male'
                control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                label={genderTable.male}
              />
              <FormControlLabel
                value='female'
                control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                label={genderTable.female}
              />
              <FormControlLabel
                value='other'
                control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                label={genderTable.other}
              />
            </RadioGroup>
          )}
        />
      </FormControl>

      <Controller
        name={FormFields.birthdate.name}
        control={control}
        rules={{ required: FormFields.birthdate.required }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            label={FormFields.birthdate.label}
            errorText={fieldState.error?.message}
            inputProps={{
              tabIndex: FormFields.birthdate.tabIndex,
              required: FormFields.birthdate.required,
            }}
            type={FormFields.birthdate.type}
            size='small'
            fullWidth
            onBlur={() => {
              field.onBlur();
              normalizeAndValidateDate<PatientFormValues>(FormFields.birthdate.name, methods);
            }}
            onChange={(evt) => {
              field.onChange(evt);
              clearErrors(FormFields.birthdate.name);
            }}
          />
        )}
      />

      <Controller
        name={FormFields.email.name}
        control={control}
        rules={{ required: FormFields.email.required }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            label={FormFields.email.label}
            errorText={fieldState.error?.message}
            inputProps={{
              tabIndex: FormFields.email.tabIndex,
              required: FormFields.email.required,
              maxLength: FormFields.email.maxLength,
              pattern: FormFields.email.pattern,
            }}
            type={FormFields.email.type}
            size='small'
            fullWidth
          />
        )}
      />

      <div className='register-form__terms-wrapper'>
        <Checkbox sx={{ pt: '10px' }} checked={checked} onChange={(evt) => setChecked(evt.target.checked)} required />
        <span>
          <Link href={dataset.termsPrivacyLink} underline='hover' target='_blank'>
            個人情報の取扱
          </Link>
          と
          <Link href={dataset.termsEulaLink} underline='hover' target='_blank'>
            利用規約
          </Link>
          に同意します。
        </span>
      </div>

      <input
        className='btn btn--main'
        id='accept-terms-button'
        type='submit'
        value={dataset.confirmationPage ? '登録してオンラインテストへ進む' : '登録'}
        disabled={!checked}
      />
    </form>
  );
};

export default PatientRegister;
